import React, { useState } from 'react';
import { DrawingCanvas } from './DrawingCanvas';
import { MeasurementTools } from './MeasurementTools';
import { FileUpload } from './FileUpload';
import { RoomQuickSetupModal } from './RoomQuickSetupModal';
import { UploadIcon, RulerIcon, CheckIcon } from 'lucide-react';
interface RoomBuilderProps {
  onComplete: (roomData: any) => void;
}
export function RoomBuilder({
  onComplete
}: RoomBuilderProps) {
  const [activeTab, setActiveTab] = useState('draw'); // 'draw' or 'upload'
  const [roomMeasurements, setRoomMeasurements] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [showQuickSetupModal, setShowQuickSetupModal] = useState(true);
  const handleAddWall = wall => {
    const newMeasurements = [...roomMeasurements, wall];
    setRoomMeasurements(newMeasurements);
    // Check if room is complete (closed polygon)
    if (newMeasurements.length >= 3) {
      const firstPoint = newMeasurements[0].start;
      const lastPoint = newMeasurements[newMeasurements.length - 1].end;
      // Simple check if first and last points are close enough
      if (Math.abs(firstPoint.x - lastPoint.x) < 10 && Math.abs(firstPoint.y - lastPoint.y) < 10) {
        setIsValid(true);
      }
    }
  };
  const handleFileUpload = file => {
    // Handle PDF processing here
    console.log('File uploaded:', file);
    // Mock room data from file
    setIsValid(true);
  };
  const handleComplete = () => {
    // Create a sample room data if none exists to allow progression
    const roomData = {
      walls: roomMeasurements.length > 0 ? roomMeasurements : createDefaultRoom()
    };
    onComplete(roomData);
  };
  // Create a default room if none is drawn
  const createDefaultRoom = () => {
    const centerX = 400;
    const centerY = 300;
    const width = 300;
    const height = 200;
    return [{
      start: {
        x: centerX - width / 2,
        y: centerY - height / 2
      },
      end: {
        x: centerX + width / 2,
        y: centerY - height / 2
      },
      length: width
    }, {
      start: {
        x: centerX + width / 2,
        y: centerY - height / 2
      },
      end: {
        x: centerX + width / 2,
        y: centerY + height / 2
      },
      length: height
    }, {
      start: {
        x: centerX + width / 2,
        y: centerY + height / 2
      },
      end: {
        x: centerX - width / 2,
        y: centerY + height / 2
      },
      length: width
    }, {
      start: {
        x: centerX - width / 2,
        y: centerY + height / 2
      },
      end: {
        x: centerX - width / 2,
        y: centerY - height / 2
      },
      length: height
    }];
  };
  const handleQuickSetup = roomData => {
    setRoomMeasurements(roomData.walls);
    setIsValid(true);
    setShowQuickSetupModal(false);
  };
  return <div className="flex h-full" data-id="element-748">
      <div className="w-64 bg-white border-r border-gray-200 shadow-sm overflow-y-auto" data-id="element-749">
        <div className="p-4" data-id="element-750">
          <h2 className="text-lg font-semibold text-gray-700 mb-4" data-id="element-751">
            Room Builder
          </h2>
          <div className="flex border-b border-gray-200 mb-4" data-id="element-752">
            <button className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'draw' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('draw')} data-id="element-753">
              <RulerIcon className="h-4 w-4 mr-2" data-id="element-754" />
              <span data-id="element-755">Manual Drawing</span>
            </button>
            <button className={`flex items-center px-4 py-2 border-b-2 ${activeTab === 'upload' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('upload')} data-id="element-756">
              <UploadIcon className="h-4 w-4 mr-2" data-id="element-757" />
              <span data-id="element-758">Upload File</span>
            </button>
          </div>
          {activeTab === 'draw' ? <MeasurementTools onAddWall={handleAddWall} data-id="element-759" /> : <FileUpload onFileUpload={handleFileUpload} data-id="element-760" />}
          {roomMeasurements.length > 0 && <div className="mt-6 border-t border-gray-200 pt-4" data-id="element-761">
              <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-762">
                Room Dimensions
              </h3>
              <ul className="space-y-2 text-sm" data-id="element-763">
                {roomMeasurements.map((wall, index) => <li key={index} className="flex justify-between" data-id="element-764">
                    <span data-id="element-765">Wall {index + 1}</span>
                    <span className="font-medium" data-id="element-766">{wall.length} cm</span>
                  </li>)}
              </ul>
            </div>}
          <div className="mt-6" data-id="element-767">
            <button onClick={handleComplete} className="w-full py-2 px-4 rounded-md flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white" data-id="element-768">
              <CheckIcon className="h-4 w-4 mr-2" data-id="element-769" />
              <span data-id="element-770">Continue to Kitchen Design</span>
            </button>
          </div>
        </div>
      </div>
      <div className="flex-1 relative" data-id="element-771">
        <DrawingCanvas activeTab={activeTab} measurements={roomMeasurements} onAddWall={handleAddWall} data-id="element-772" />
      </div>
      {/* Quick Setup Modal */}
      {showQuickSetupModal && <RoomQuickSetupModal onClose={() => setShowQuickSetupModal(false)} onApply={handleQuickSetup} data-id="element-773" />}
    </div>;
}